import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const PTERO_STATE_MAP = {
  running:  'running',
  stopping: 'stopped',
  offline:  'stopped',
  starting: 'starting',
};

export const useResources = (serverId, initialStatus = 'stopped') => {
  const [resources, setResources] = useState(null);
  const [liveStatus, setLiveStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchResources = useCallback(async () => {
    if (!serverId) return;
    try {
      const { data } = await api.get(`/servers/${serverId}/resources`);
      setResources(data);
      if (data?.status) {
        const mapped = PTERO_STATE_MAP[data.status] || data.status;
        setLiveStatus(mapped);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => {
    setLiveStatus(initialStatus);
  }, [initialStatus]);

  useEffect(() => {
    if (!serverId) return;
    fetchResources();
    intervalRef.current = setInterval(fetchResources, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchResources]);

  const formatBytes = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / 1048576;
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb.toFixed(0)} MB`;
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return { resources, loading, liveStatus, formatBytes, formatUptime, refetch: fetchResources };
};
